import { TFunction } from 'i18next';
import {
  BasisOfCoverResponseDtoBasisOfCoverEnum,
  ContractResponseDto,
  ContractResponseDtoContractStatusEnum,
  LayerResponseDtoLimitTypeEnum,
  LayerResultsResponseDto,
  UpdateContractPayloadDtoBasisOfCoverEnum,
} from 'libs/pinnacle-sdk';
import { isNil } from 'lodash';
import * as yup from 'yup';
import { isCancelledOrNTUContract } from '../validation-helper';

export const calculateAdjustedRate = (
  layerPremium?: string,
  layerType?: LayerResponseDtoLimitTypeEnum,
  totalTiv?: string
): number | undefined => {
  const totalTivValue = totalTiv ? Number(totalTiv) : undefined;

  if (
    (layerType === LayerResponseDtoLimitTypeEnum.Primary ||
      layerType === LayerResponseDtoLimitTypeEnum.QuotaShare) &&
    layerPremium &&
    totalTivValue
  ) {
    return (Number(layerPremium) / totalTivValue) * 100;
  }

  return 0;
};

export interface BinderPlacementFormValues {
  binderShareSigned?: number;
  rateMovement?: number;
  originalRate: number;
  adjustedRate?: number;
  totalInsurableValue?: string;
  layerType?: string;
  limit?: string;
  excess?: string;
  deductible?: string;
  layerPremium?: string;
  binderShareQuoted?: number;
  shareOfLimitSigned?: number;
  shareOfPremiumSigned?: number;
  rateAdequacy?: string;
  basisOfCover?: UpdateContractPayloadDtoBasisOfCoverEnum;
}

export const getInitialBinderPlacement = (
  contract?: ContractResponseDto,
  totalTiv?: string,
  layerResult?: LayerResultsResponseDto
): BinderPlacementFormValues => {
  const rateAdequacy =
    layerResult?.rateAdequacy ??
    (contract?.rateAdequacy ? Number(contract.rateAdequacy) * 100 : undefined);

  return {
    binderShareSigned: isNil(contract?.binderShareSigned)
      ? undefined
      : Number((Number(contract?.binderShareSigned) * 100).toFixed(4)),
    rateMovement: isNil(contract?.rateMovement)
      ? undefined
      : Number(contract?.rateMovement) * 100,
    originalRate: isNil(contract?.originalRate)
      ? 0
      : Number(contract?.originalRate) * 100,
    adjustedRate: isNil(contract?.adjustedRate)
      ? calculateAdjustedRate(
          contract?.layer?.premium,
          contract?.layer?.limitType,
          totalTiv
        )
      : Number(contract?.adjustedRate) * 100,
    totalInsurableValue: totalTiv ? Number(totalTiv).toFixed(2) : undefined,
    layerType: contract?.layer?.limitType,
    limit: contract?.layer?.limit,
    excess: contract?.layer?.excess,
    deductible: contract?.layer?.deductibles,
    layerPremium: contract?.layer?.premium,
    binderShareQuoted: isNil(contract?.binderShareQuoted)
      ? undefined
      : Number((Number(contract?.binderShareQuoted) * 100).toFixed(2)),
    rateAdequacy: rateAdequacy ? Number(rateAdequacy).toFixed(2) : undefined,
    basisOfCover: contract?.basisOfCover,
  };
};

export const getBinderPlacementValidationSchemaShape = (t: TFunction) => ({
  binderShareSigned: yup.number().when('contractStatus', {
    is: (contractStatus: ContractResponseDtoContractStatusEnum) =>
      !isCancelledOrNTUContract(contractStatus),
    then: (schema) =>
      schema
        .required(
          t('validation.required', {
            field: t('contractDetail.binderShareSigned'),
          })
        )
        .moreThan(
          0,
          t('validation.min', {
            field: t('contractDetail.binderShareSigned'),
            min: 0,
          })
        )
        .max(
          100,
          t('validation.max', {
            field: t('contractDetail.binderShareSigned'),
            max: 100,
          })
        ),
    otherwise: (schema) => schema.nullable(),
  }),
  rateMovement: yup.number().when('contractStatus', {
    is: (contractStatus: ContractResponseDtoContractStatusEnum) =>
      !isCancelledOrNTUContract(contractStatus),
    then: (schema) =>
      schema.required(
        t('validation.required', {
          field: t('contractDetail.rateMovement'),
        })
      ),
    otherwise: (schema) => schema.nullable(),
  }),
  originalRate: yup.number().when('contractStatus', {
    is: (contractStatus: ContractResponseDtoContractStatusEnum) =>
      !isCancelledOrNTUContract(contractStatus),
    then: (schema) =>
      schema
        .required(
          t('validation.required', { field: t('contractDetail.originalRate') })
        )
        .min(
          0,
          t('validation.min', {
            field: t('contractDetail.originalRate'),
            min: 0,
          })
        ),
    otherwise: (schema) => schema.nullable(),
  }),
  adjustedRate: yup
    .number()
    .nullable()
    .min(
      0,
      t('validation.min', { field: t('contractDetail.adjustedRate'), min: 0 })
    ),
  totalInsurableValue: yup.mixed().when('contractStatus', {
    is: (contractStatus: ContractResponseDtoContractStatusEnum) =>
      !isCancelledOrNTUContract(contractStatus),
    then: (schema) =>
      schema.required(
        t('validation.required', {
          field: t('contractDetail.totalInsurableValue'),
        })
      ),
    otherwise: (schema) => schema.nullable(),
  }),
  layerType: yup.string(),
  limit: yup.string(),
  excess: yup.string(),
  deductible: yup.string(),
  layerPremium: yup
    .number()
    .nullable()
    .min(
      0,
      t('validation.min', { field: t('contractDetail.layerPremium'), min: 0 })
    ),
  binderShareQuoted: yup
    .number()
    .nullable()
    .min(
      0,
      t('validation.min', {
        field: t('contractDetail.binderShareWritten'),
        min: 0,
      })
    ),
  shareOfLimitSigned: yup.string(),
  shareOfPremiumSigned: yup.string(),
  rateAdequacy: yup.string(),

  basisOfCover: yup.string().when('contractStatus', {
    is: (contractStatus: ContractResponseDtoContractStatusEnum) =>
      !isCancelledOrNTUContract(contractStatus),
    then: () =>
      yup
        .string()
        .required(
          t('validation.required', { field: t('contractDetail.basisOfCover') })
        )
        .oneOf(
          Object.values(BasisOfCoverResponseDtoBasisOfCoverEnum),
          t('validation.invalidOption', {
            field: t('contractDetail.basisOfCover'),
          })
        ),
    otherwise: () => yup.string().nullable(),
  }),
});

export const validateBinderPlacementWarnings = (
  values: Partial<BinderPlacementFormValues>,
  t: TFunction
): Record<string, string> => {
  const warnings: Record<string, string> = {};

  if (
    values.binderShareSigned &&
    values.binderShareQuoted &&
    values.binderShareSigned > values.binderShareQuoted &&
    values.binderShareSigned <= 100
  ) {
    warnings['binderShareSigned'] = t(
      'validation.binderShareSignedExceedsWritten'
    );
  }

  return warnings;
};
